import { InlineAlert } from '../../components/BaseComponents'
import { useCallback, useRef, useState, useEffect, useLayoutEffect } from "react";
import { Button, LoadingSpinner } from "../../components/BaseComponents";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import useApi from "../../hooks/useApi";
import useAddNotification from "../../hooks/useAddNotification";
import { getCSRFToken } from "../../lib/utils";
import { FOCUS_VISIBLE_RING_INSET } from "../../lib/a11yFocus";
import { getTrainingUsersTemplateMetadata, CsvTemplateMetadata } from "../../lib/api/csvTemplates";
import { CsvColumn } from "../../types/CsvTemplate";

type CsvRowError = {
    reference: string,
    text: string,
};

type UploadTrainingUsersModalProps = {
    trainingSessionId: string,
    onClose: () => void,
    onUploaded: () => void,
    // #873: called instead of onUploaded when the upload created rows but also produced
    // skipped-row errors and/or email-delivery warnings that still need to be shown. Refreshes
    // the underlying list without closing this modal out from under the admin. Falls back to
    // onUploaded if not supplied, so existing callers keep working unchanged.
    onUploadedWithIssues?: () => void,
    isOpen?: boolean,
};

// Reuses AddPurchasesUpload.tsx's preview/per-row-error UX and the CSV template
// download + guidance accordion pattern from CompanyPurchases.tsx.
function UploadTrainingUsersModal({ trainingSessionId, onClose, onUploaded, onUploadedWithIssues, isOpen }: UploadTrainingUsersModalProps) {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<CsvRowError[]>([]);
    const [warnings, setWarnings] = useState<CsvRowError[]>([]);
    // Distinguishes "some rows were skipped but others WERE created" from "the whole file was
    // rejected, no users were created" - both populate `errors`, but the message shown (and
    // whether it's safe to imply nothing happened) must not conflate the two.
    const [succeededWithIssues, setSucceededWithIssues] = useState(false);
    const [networkError, setNetworkError] = useState<string | null>(null);
    const [showGuidance, setShowGuidance] = useState(false);
    const [templateMetadata, setTemplateMetadata] = useState<CsvTemplateMetadata | null>(null);
    const [templateLoading, setTemplateLoading] = useState(true);
    const [templateError, setTemplateError] = useState(false);
    const [templateApi, setTemplateApi] = useState<ReturnType<typeof useApi>>();
    const [isDragActive, setIsDragActive] = useState(false);
    const [invalidFileType, setInvalidFileType] = useState(false);
    const inputFile = useRef<HTMLInputElement>(null);
    const api = useApi();
    const notify = useAddNotification();
    const notifyRef = useRef(notify);
    notifyRef.current = notify;
    const pending = useRef<{ controller: AbortController; submitted: boolean }>();
    const interrupted = useRef(false);
    const context = useRef({ api, trainingSessionId, isOpen, generation: 0 });
    if (context.current.api !== api || context.current.trainingSessionId !== trainingSessionId || context.current.isOpen !== isOpen) {
        context.current = { api, trainingSessionId, isOpen, generation: context.current.generation + 1 };
    }

    const reset = useCallback(() => {
        setFile(null);
        setErrors([]);
        setWarnings([]);
        setSucceededWithIssues(false);
        setNetworkError(null);
        setInvalidFileType(false);
        if (inputFile.current) {
            inputFile.current.value = '';
        }
    }, []);

    const abortUpload = useCallback(() => {
        const request = pending.current;
        if (!request) return;
        pending.current = undefined;
        request.controller.abort();
        if (request.submitted) {
            interrupted.current = true;
            notifyRef.current({ body: 'The upload was interrupted. Some rows may already have been added. Check the training session before trying again.' });
        }
    }, []);

    // Clear the previous session's file and results before the new context is painted.
    useLayoutEffect(() => {
        reset();
        setLoading(false);
        if (isOpen && interrupted.current) {
            setNetworkError('The previous upload was interrupted. Some rows may already have been added; check the previous training session before trying again.');
            interrupted.current = false;
        }
        return () => abortUpload();
    }, [api, trainingSessionId, isOpen, reset, abortUpload]);

    useEffect(() => {
        const controller = new AbortController();
        setTemplateApi(api);
        setTemplateLoading(true);
        setTemplateError(false);
        setTemplateMetadata(null);
        const fetchTemplate = async () => {
            try {
                const metadata = await getTrainingUsersTemplateMetadata(api, { signal: controller.signal });
                if (!controller.signal.aborted) setTemplateMetadata(metadata);
            } catch {
                if (!controller.signal.aborted) {
                    console.warn('Failed to fetch CSV template metadata.');
                    setTemplateError(true);
                }
            } finally {
                if (!controller.signal.aborted) setTemplateLoading(false);
            }
        };

        fetchTemplate();
        return () => controller.abort();
    }, [api]);

    const handleFileSelection = (selectedFile: File | null) => {
        setErrors([]);
        setWarnings([]);
        setSucceededWithIssues(false);
        setNetworkError(null);
        if (selectedFile) {
            if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
                setFile(selectedFile);
                setInvalidFileType(false);
            } else {
                setInvalidFileType(true);
                setFile(null);
            }
        } else {
            setFile(null);
            setInvalidFileType(false);
        }
    }

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    }

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles.length > 0) {
            handleFileSelection(droppedFiles[0]);
        }
    }

    const upload = async () => {
        if (!file || pending.current) {
            return;
        }
        const request = { controller: new AbortController(), submitted: false };
        const generation = context.current.generation;
        pending.current = request;
        setLoading(true);
        setErrors([]);
        setWarnings([]);
        setSucceededWithIssues(false);
        setNetworkError(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('_csrf', await getCSRFToken(request.controller.signal));
            if (request.controller.signal.aborted || generation !== context.current.generation) return;

            request.submitted = true;
            const body = await api.form(
                `admin/training-sessions/${trainingSessionId}/users/bulk-upload`,
                formData,
                { signal: request.controller.signal },
            );
            if (request.controller.signal.aborted || generation !== context.current.generation) return;

            if (body.success === true) {
                const rowErrors: CsvRowError[] = body.data?.errors || [];
                const rowWarnings: CsvRowError[] = body.data?.warnings || [];
                if (rowErrors.length === 0 && rowWarnings.length === 0) {
                    reset();
                    onUploaded();
                } else {
                    // #873: rows were created, but some were skipped and/or some invitation
                    // emails couldn't be confirmed as sent - never silently discard those, and
                    // never auto-close the modal out from under the admin before they can read
                    // them. The underlying list still needs refreshing since rows WERE created.
                    setFile(null);
                    if (inputFile.current) {
                        inputFile.current.value = '';
                    }
                    setErrors(rowErrors);
                    setWarnings(rowWarnings);
                    setSucceededWithIssues(true);
                    (onUploadedWithIssues ?? onUploaded)();
                }
            } else if (Array.isArray(body.errors) && body.errors.length > 0) {
                setErrors(body.errors);
            } else {
                setFile(null);
                if (inputFile.current) inputFile.current.value = '';
                setNetworkError('We could not confirm the upload outcome. Some rows may already have been added. Refresh the user list before trying this file again.');
                onUploadedWithIssues?.();
            }
        } catch {
            if (request.controller.signal.aborted || generation !== context.current.generation) return;
            // #873: some rows may have been committed before the response was lost. Require a
            // deliberate reselect before retrying and refresh the list without closing this modal.
            setFile(null);
            if (inputFile.current) {
                inputFile.current.value = '';
            }
            setNetworkError('We could not confirm the upload outcome. Some rows may already have been added. Refresh the user list before trying this file again.');
            onUploadedWithIssues?.();
        } finally {
            if (pending.current === request) {
                pending.current = undefined;
                setLoading(false);
            }
        }
    }

    const close = () => {
        context.current.generation += 1;
        abortUpload();
        reset();
        setLoading(false);
        onClose();
    }

    return (
        <CanonicalModal
            open={!!isOpen}
            onClose={close}
            size="small"
            title="Import users (CSV)"
            isDragActive={isDragActive}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            actions={
                <>
                    <Button
                        onClick={upload}
                        additionalClass="mr-5"
                        disabled={!file || loading}
                        type="Primary"
                        text="Upload"
                    />
                    <Button onClick={close} type="Outlined" text="Cancel" />
                </>
            }
        >
            <div className="flex flex-wrap gap-4 mt-4">
                <Button
                    onClick={() => {
                        window.location.href = `${process.env.REACT_APP_BASE_API}/admin/training-sessions/users-csv-template`
                    }}
                    type="Primary"
                    hasLeadingIcon
                    icon="fa-solid fa-download"
                    text="Download CSV template"
                />
                <input
                    type="file"
                    id="trainingUsersCsvFile"
                    aria-label="Select CSV file to upload"
                    ref={inputFile}
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => {
                        handleFileSelection(e.target.files && e.target.files.length > 0 ? e.target.files[0] : null);
                    }}
                />
                <Button
                    onClick={() => inputFile.current?.click()}
                    type="Outlined"
                    text="Select CSV file..."
                    hasLeadingIcon
                    icon="fa-solid fa-upload"
                />
            </div>

            <p className="text-charcoal-100 text-sm mt-4">(You can drag & drop a CSV file onto this area)</p>

            <div className="mt-6 rounded-lg bg-charcoal-700 p-4 text-white">
                <button
                    type="button"
                    aria-controls="trainingUsersCsvGuidance"
                    aria-expanded={showGuidance}
                    className={`flex items-center justify-between w-full text-left cursor-pointer px-1.5 ${FOCUS_VISIBLE_RING_INSET}`}
                    onClick={() => setShowGuidance(!showGuidance)}
                >
                    <h2 className="text-sm">Data collection and CSV format</h2>
                    <i className={`fa-solid ${showGuidance ? 'fa-minus' : 'fa-plus'} text-algae-300`} />
                </button>
                <div
                    id="trainingUsersCsvGuidance"
                    className={`${!showGuidance && 'hidden'} border-t border-charcoal-400 mt-4 pt-2 text-sm`}
                >
                    {templateApi !== api || templateLoading ? (
                        <p className="text-charcoal-100 py-2">Loading field information...</p>
                    ) : templateMetadata?.template && templateMetadata.template.length > 0 ? (
                        <>
                            <ul className="pl-4 list-disc">
                                {templateMetadata.template.map((column: CsvColumn) => (
                                    <li key={column.name} className="mb-2 text-charcoal-100">
                                        <span className="bg-charcoal-400 rounded px-1.5 py-0.5 text-white text-sm font-mono">{column.name}</span> {column.description || column.name}
                                        {column.isRequired ? (
                                            <span className="text-algae-600"> (MANDATORY)</span>
                                        ) : (
                                            <span className="text-algae-600"> (optional)</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <p className="text-charcoal-100 mt-2">
                                Max {templateMetadata.maxRowsPerUpload} rows per upload.
                            </p>
                        </>
                    ) : (
                        <>
                            {templateError && (
                                <InlineAlert type='error' className='mb-4'>
                                    Current field information could not be loaded. Check the template and row limit before uploading.
                                </InlineAlert>
                            )}
                            <ul className="pl-4 list-disc">
                                <li className="mb-2 text-charcoal-100">
                                    <span className="bg-charcoal-400 rounded px-1.5 py-0.5 text-white text-sm font-mono">EMAIL</span> user email address
                                    <span className="text-algae-600"> (MANDATORY)</span>
                                </li>
                                <li className="mb-2 text-charcoal-100">
                                    <span className="bg-charcoal-400 rounded px-1.5 py-0.5 text-white text-sm font-mono">organization_name</span> custom organisation name
                                    <span className="text-algae-600"> (optional)</span>
                                </li>
                            </ul>
                            <p className="text-charcoal-100 mt-2">
                                {templateMetadata ? `Max ${templateMetadata.maxRowsPerUpload} rows per upload. ` : ''}
                            </p>
                        </>
                    )}
                    <p className="text-charcoal-100 mt-2">
                        Invalid CSV fields reject the whole file. Duplicate invitations and existing training members are skipped; other valid rows can still be added. Check the results and user list before retrying, and upload only rows that still need to be added.
                    </p>
                </div>
            </div>

            { loading && <LoadingSpinner /> }
            { invalidFileType && (
                <div className="bg-warning-050 text-warning-800 rounded p-4 mt-4 text-sm">
                    <p>Only CSV files are supported.</p>
                </div>
            )}
            { networkError && (
                <InlineAlert type='warning' className='mt-4'>
                    <p>{ networkError }</p>
                </InlineAlert>
            )}
            { file && !errors.length && (
                <div className="bg-charcoal-700 text-charcoal-100 rounded p-4 mt-4 text-sm">
                    <p>File selected: <span className="bg-charcoal-400 rounded px-2 py-1 text-white font-mono">{file.name}</span></p>
                </div>
            )}
            { errors.length > 0 && (
                <InlineAlert type='error' className='mt-4'>
                    <p className="mb-2">
                        { succeededWithIssues
                            ? 'Some rows were not imported:'
                            : 'This file was rejected - no users were created:' }
                    </p>
                    <ul className="list-disc pl-4">
                        { errors.map((error, index) => (
                            <li key={`${error.reference}-${index}`}>
                                <strong>{ error.reference }</strong>: { error.text }
                            </li>
                        ))}
                    </ul>
                </InlineAlert>
            )}
            { warnings.length > 0 && (
                <InlineAlert type='warning' className='mt-4'>
                    <p className="mb-2">Users were added, but some invitation emails could not be confirmed as sent:</p>
                    <ul className="list-disc pl-4">
                        { warnings.map((warning, index) => (
                            <li key={`${warning.reference}-${index}`}>
                                <strong>{ warning.reference }</strong>: { warning.text }
                            </li>
                        ))}
                    </ul>
                </InlineAlert>
            )}
        </CanonicalModal>
    )
}

export default UploadTrainingUsersModal;
